<template>
  <ViceButton
    @click="handleExternalCameraConnection"
    circle
    white
    :showNotification="isActive"
    style="padding: 0 !important"
    :btnDescription="btnDescription"
    :class="{ 'sm:bg-vice-teal-600 hover:bg-vice-teal-700': isActive }"
    class="w-12 h-12 border-none bg-vice-light-grey"
  >
    <ViceLoader colorClass="text-vice-teal-700" class="left-2" v-if="loading" />

    <template v-else>
      <ViceCameraIcon class="w-12 h-12" :pathFill="!isActive ? '#0F766E' : '#fff'" />
    </template>
  </ViceButton>
</template>

<script>
import { computed, ref } from 'vue';
import _filter from 'lodash/filter';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import ViceButton from '@/Components/Buttons/ViceButton.vue';
import ViceCameraIcon from '../Icons/ViceCamera.vue';
import ViceLoader from '../Loaders/ViceLoader.vue';
import { generateCameraData } from '@/Utils/helpers';
import { CAMERA_TYPES } from '@/Utils/vice-constants';
import useSessionStore from '@/Stores/useSessionStore';

export default {
  name: 'AddExternalCameraButton',
  components: {
    ViceButton,
    ViceCameraIcon,
    ViceLoader,
  },
  setup() {
    const loading = ref(false);

    const sessionStore = useSessionStore();
    const multiCameraDevicesStore = useLocalStreamingDevicesStore();

    const isActive = computed(() => multiCameraDevicesStore.allCameraTypeNames.length >= 2);
    const btnDescription = computed(() =>
      multiCameraDevicesStore.allCameraTypeNames.length >= 2
        ? 'Disconnect external camera'
        : 'Connect external camera',
    );
    const externalCameraName = computed(() => {
      const numberOfCameras = Number(Object.keys(multiCameraDevicesStore.allCameraTypes).length);
      if (numberOfCameras === 1) return 'External Camera';
      return `${CAMERA_TYPES.EXTERNAL_WEBCAM} ${numberOfCameras}`;
    });

    const addExternalCamera = (name, isAddedOnInit = false, cameraId = 'default') => {
      multiCameraDevicesStore.setCurrentActiveCameraType(name);
      const camData = generateCameraData(
        name,
        multiCameraDevicesStore.allCameraTypes[name]?.cameraDeviceId || cameraId, // TODO: Get id for the dermatoscope
        isAddedOnInit,
      );
      multiCameraDevicesStore.addCameraType(name, {
        [name]: camData,
      });

      if (!isAddedOnInit) multiCameraDevicesStore.openPreviewModal('Camera');
    };

    const handleExternalCameraConnection = () => {
      try {
        loading.value = true;
        sessionStore.toggleControlPanel(true);
        if (multiCameraDevicesStore.connectedCameras.length < 2) {
          addExternalCamera(externalCameraName.value);
        } else {
          // removes all external cameras
          const externalCameras = _filter(
            multiCameraDevicesStore.connectedCameras,
            (cameraType) => cameraType.name !== CAMERA_TYPES.INTERNAL_WEBCAM,
          );

          externalCameras.forEach(async (camType) => {
            await multiCameraDevicesStore.removeCamera(camType.name);
          });

          multiCameraDevicesStore.markSessionSetupAsChanged();
        }
      } catch (error) {
        console.error({ error });
      } finally {
        loading.value = false;
      }
    };

    return {
      loading,
      isActive,
      btnDescription,
      handleExternalCameraConnection,
    };
  },
};
</script>
